import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useApp, dateForOffset } from '../state/app.js';
import { Spinner } from '../ui/common.js';

// ─────────────────────────────────────────────────────────────────────────────
// 랜덤피커 (사다리 게임 / Amidakuji)
//  1) 임직원 대상 중 참여자 선택
//  2) 참여자 수만큼 상품/상금/벌칙 결과 지정
//  3) 사다리 무작위 생성
//  4) 한 사람씩 결과 확인 (경로 추적 애니메이션)
// 참여자 풀은 전 직원 접근 가능한 board 엔드포인트에서 이름·부서를 가져오고,
// 비어 있으면(개발/mock) 데모 명단으로 폴백한다.
// ─────────────────────────────────────────────────────────────────────────────

type ResultKind = 'prize' | 'cash' | 'penalty' | 'none';
interface ResultSlot { kind: ResultKind; label: string; }
interface Person { name: string; dept?: string; }
interface Point { x: number; y: number; }

const KIND_META: Record<ResultKind, { label: string; color: string; bg: string; emoji: string }> = {
  prize: { label: '상품', color: '#047857', bg: '#ECFDF5', emoji: '🎁' },
  cash: { label: '상금', color: '#1D4ED8', bg: '#EFF4FE', emoji: '💰' },
  penalty: { label: '벌칙', color: '#B91C1C', bg: '#FEF2F2', emoji: '⚡' },
  none: { label: '꽝', color: '#6B7280', bg: '#F3F4F6', emoji: '💨' },
};

// 참여자별 경로 색상 팔레트
const LINE_COLORS = ['#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED', '#DB2777', '#0891B2', '#CA8A04', '#4F46E5', '#EA580C'];

const DEMO_ROSTER: Person[] = [
  { name: '김도현', dept: '개발팀' }, { name: '이서연', dept: '개발팀' }, { name: '박준호', dept: '디자인팀' },
  { name: '최민지', dept: '기획팀' }, { name: '정우성', dept: '영업팀' }, { name: '강하늘', dept: '경영지원' },
  { name: '윤아름', dept: '마케팅' }, { name: '임재현', dept: '개발팀' },
];

const DEFAULT_RESULTS: ResultSlot[] = [
  { kind: 'prize', label: '치킨 기프티콘' },
  { kind: 'cash', label: '10,000원' },
  { kind: 'none', label: '꽝' },
  { kind: 'penalty', label: '커피 사기' },
];

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── 사다리 모델 ──────────────────────────────────────────────────────────────
// rungs[r][c] = true 이면 row r 에서 열 c 와 c+1 사이 가로 발판. 인접 발판 금지.
function buildRungs(cols: number, rows: number): boolean[][] {
  const rungs: boolean[][] = [];
  for (let r = 0; r < rows; r++) {
    const row = new Array<boolean>(Math.max(cols - 1, 0)).fill(false);
    for (let c = 0; c < cols - 1; c++) {
      if (c > 0 && row[c - 1]) continue; // 인접 발판 방지
      if (Math.random() < 0.5) row[c] = true;
    }
    rungs.push(row);
  }
  // 각 열이 최소 1개 발판을 갖도록 보정(단조로운 직선 경로 방지)
  for (let c = 0; c < cols - 1; c++) {
    const has = rungs.some((row) => row[c]);
    if (!has) {
      for (let tries = 0; tries < 20; tries++) {
        const r = Math.floor(Math.random() * rows);
        if ((c === 0 || !rungs[r][c - 1]) && (c >= cols - 2 || !rungs[r][c + 1])) { rungs[r][c] = true; break; }
      }
    }
  }
  return rungs;
}

interface Geo { cols: number; rows: number; colX: (c: number) => number; rowY: (r: number) => number; topY: number; botY: number; width: number; height: number; }

function makeGeo(cols: number, rows: number): Geo {
  const colGap = 92;
  const padX = 46;
  const rowGap = 24; // 세로 간격 축소(한 화면 표시)
  const topY = 50;
  // 발판을 이름 바로 아래(첫 행)~결과 바로 위(마지막 행)까지 꽉 채워 상/하 여백 제거
  const botY = topY + rows * rowGap;
  const width = padX * 2 + (cols - 1) * colGap;
  const height = botY + 60;
  return {
    cols, rows, topY, botY, width, height,
    colX: (c) => padX + c * colGap,
    rowY: (r) => topY + (r + 0.5) * rowGap,
  };
}

function computePath(rungs: boolean[][], start: number, geo: Geo): { points: Point[]; end: number } {
  let cur = start;
  const pts: Point[] = [{ x: geo.colX(cur), y: geo.topY }];
  for (let r = 0; r < geo.rows; r++) {
    const y = geo.rowY(r);
    pts.push({ x: geo.colX(cur), y });
    if (cur > 0 && rungs[r][cur - 1]) { cur -= 1; pts.push({ x: geo.colX(cur), y }); }
    else if (cur < geo.cols - 1 && rungs[r][cur]) { cur += 1; pts.push({ x: geo.colX(cur), y }); }
  }
  pts.push({ x: geo.colX(cur), y: geo.botY });
  return { points: pts, end: cur };
}

function pointsToPath(pts: Point[]): string {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
}

// ── 화면 ─────────────────────────────────────────────────────────────────────
export function RandomPicker(): JSX.Element {
  const { flash } = useApp();
  const today = dateForOffset(0).date;
  const board = useQuery({ queryKey: ['board', today], queryFn: () => api.board(today) });

  const roster: Person[] = useMemo(() => {
    const entries = board.data?.entries ?? [];
    if (entries.length > 0) return entries.map((e) => ({ name: e.name, dept: e.dept }));
    return DEMO_ROSTER;
  }, [board.data]);

  const [selected, setSelected] = useState<string[]>([]);
  const [results, setResults] = useState<ResultSlot[]>(DEFAULT_RESULTS.slice(0, 0));
  const [ladder, setLadder] = useState<{ rungs: boolean[][]; geo: Geo; players: string[]; slots: ResultSlot[] } | null>(null);
  const [search, setSearch] = useState('');

  // 참여자 수가 바뀌면 결과 슬롯 개수 동기화
  useEffect(() => {
    setResults((prev) => {
      const n = selected.length;
      if (prev.length === n) return prev;
      if (prev.length < n) {
        const add = Array.from({ length: n - prev.length }, (_, i) => DEFAULT_RESULTS[(prev.length + i) % DEFAULT_RESULTS.length]);
        return [...prev, ...add.map((s) => ({ ...s }))];
      }
      return prev.slice(0, n);
    });
  }, [selected.length]);

  const toggle = (name: string) => {
    setLadder(null);
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  const filtered = roster.filter((p) => !search || p.name.includes(search) || (p.dept ?? '').includes(search));

  const generate = () => {
    if (selected.length < 2) { flash('참여자를 2명 이상 선택하세요'); return; }
    if (results.some((r) => !r.label.trim())) { flash('결과(상품/상금/벌칙)를 모두 입력하세요'); return; }
    const cols = selected.length;
    const rows = Math.min(Math.max(cols + 4, 8), 14);
    const geo = makeGeo(cols, rows);
    // 결과는 무작위 배치해 상단 이름 순서와 매칭이 드러나지 않게 함
    setLadder({ rungs: buildRungs(cols, rows), geo, players: selected.slice(), slots: shuffle(results) });
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 19, fontWeight: 800, margin: '2px 0 3px' }}>랜덤피커</h2>
        <div style={{ fontSize: 13, color: '#9CA3AF' }}>
          참여자를 고르고 상품·상금·벌칙을 정한 뒤 사다리를 돌려 결과를 뽑습니다. 결과는 한 사람씩 확인할 수 있어요.
        </div>
      </div>

      {board.isLoading ? (
        <div className="dx-card" style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : !ladder ? (
        <SetupView
          roster={roster} filtered={filtered} search={search} setSearch={setSearch}
          selected={selected} toggle={toggle} clearAll={() => { setSelected([]); setLadder(null); }}
          selectAll={() => { setSelected(filtered.map((p) => p.name)); setLadder(null); }}
          results={results} setResults={setResults} onGenerate={generate}
        />
      ) : (
        <LadderView
          data={ladder}
          onReset={() => setLadder(null)}
          onRegenerate={generate}
        />
      )}
    </div>
  );
}

// ── 설정(참여자·결과) ────────────────────────────────────────────────────────
function SetupView({
  filtered, search, setSearch, selected, toggle, clearAll, selectAll, results, setResults, onGenerate,
}: {
  roster: Person[]; filtered: Person[]; search: string; setSearch: (s: string) => void;
  selected: string[]; toggle: (n: string) => void; clearAll: () => void; selectAll: () => void;
  results: ResultSlot[]; setResults: (r: ResultSlot[] | ((p: ResultSlot[]) => ResultSlot[])) => void;
  onGenerate: () => void;
}): JSX.Element {
  const setSlot = (i: number, patch: Partial<ResultSlot>) =>
    setResults((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
      {/* 참여자 선택 */}
      <div className="dx-card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '15px 18px 12px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>참여자 선택</div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#2563EB', background: '#EFF4FE', padding: '2px 9px', borderRadius: 999 }}>{selected.length}명</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={selectAll}>전체</button>
            <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={clearAll}>해제</button>
          </div>
        </div>
        <div style={{ padding: '12px 18px 0' }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="이름·부서 검색"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxHeight: 340, overflow: 'auto' }}>
          {filtered.map((p) => {
            const on = selected.includes(p.name);
            return (
              <button key={p.name} onClick={() => toggle(p.name)} style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', borderRadius: 9, cursor: 'pointer',
                border: `1px solid ${on ? '#2563EB' : '#E5E7EB'}`, background: on ? '#EFF4FE' : '#fff',
                textAlign: 'left', fontFamily: 'inherit',
              }}>
                <span style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: `1.5px solid ${on ? '#2563EB' : '#CBD5E1'}`, background: on ? '#2563EB' : '#fff', color: '#fff',
                }}>
                  {on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  {p.dept && <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>{p.dept}</span>}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && <div style={{ gridColumn: '1 / -1', padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>검색 결과가 없습니다.</div>}
        </div>
      </div>

      {/* 결과 입력 */}
      <div className="dx-card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '15px 18px 12px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>결과 지정</div>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>참여자 수만큼 · {results.length}개</span>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          {results.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>왼쪽에서 참여자를 먼저 선택하세요.</div>
          ) : results.map((s, i) => {
            const m = KIND_META[s.kind];
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 22, textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#9CA3AF' }}>{i + 1}</span>
                <select value={s.kind} onChange={(e) => setSlot(i, { kind: e.target.value as ResultKind })}
                  style={{ padding: '8px 6px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit', color: m.color, fontWeight: 700, background: m.bg }}>
                  <option value="prize">🎁 상품</option>
                  <option value="cash">💰 상금</option>
                  <option value="penalty">⚡ 벌칙</option>
                  <option value="none">💨 꽝</option>
                </select>
                <input value={s.label} onChange={(e) => setSlot(i, { label: e.target.value })} placeholder="내용 입력"
                  style={{ flex: 1, minWidth: 0, padding: '8px 11px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
            );
          })}
        </div>
        <div style={{ padding: '0 14px 16px' }}>
          <button className="dx-btn dx-btn-primary" style={{ width: '100%', padding: 13, fontSize: 14.5 }}
            disabled={selected.length < 2} onClick={onGenerate}>
            🪜 사다리 생성 ({selected.length}명)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 사다리 + 결과 확인 ───────────────────────────────────────────────────────
function LadderView({ data, onReset, onRegenerate }: {
  data: { rungs: boolean[][]; geo: Geo; players: string[]; slots: ResultSlot[] };
  onReset: () => void; onRegenerate: () => void;
}): JSX.Element {
  const { rungs, geo, players, slots } = data;
  const cols = geo.cols;

  // 각 시작열의 경로/도착 미리 계산
  const paths = useMemo(() => players.map((_, i) => computePath(rungs, i, geo)), [rungs, geo, players]);

  const [revealed, setRevealed] = useState<Record<number, number>>({}); // startCol -> endCol
  const [animCol, setAnimCol] = useState<number | null>(null);
  const [drawLen, setDrawLen] = useState(0); // 현재 애니메이션 진행 길이(px)
  const rafRef = useRef<number | null>(null);

  const allDone = Object.keys(revealed).length === players.length;

  // 애니메이션: startCol 경로를 따라 마커 이동 + 경로 그리기
  const animate = (start: number) => {
    if (animCol !== null || revealed[start] !== undefined) return;
    const pts = paths[start].points;
    const segs: number[] = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      segs.push(d); total += d;
    }
    setAnimCol(start);
    setDrawLen(0);
    const duration = Math.max(1100, total * 2.2);
    let startTs = 0;
    const step = (ts: number) => {
      if (!startTs) startTs = ts;
      const t = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 2);
      setDrawLen(eased * total);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setRevealed((prev) => ({ ...prev, [start]: paths[start].end }));
        setAnimCol(null);
        setDrawLen(0);
      }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  const revealNext = () => {
    if (animCol !== null) return;
    const next = players.findIndex((_, i) => revealed[i] === undefined);
    if (next >= 0) animate(next);
  };

  const revealAllInstant = () => {
    if (animCol !== null) return;
    const map: Record<number, number> = {};
    players.forEach((_, i) => { map[i] = paths[i].end; });
    setRevealed(map);
  };

  // 애니메이션 중 마커 위치와 그려진 경로 계산
  const animGeom = useMemo(() => {
    if (animCol === null) return null;
    const pts = paths[animCol].points;
    const drawn: Point[] = [pts[0]];
    let acc = 0;
    let marker = pts[0];
    for (let i = 1; i < pts.length; i++) {
      const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      if (acc + d <= drawLen) {
        drawn.push(pts[i]); acc += d; marker = pts[i];
      } else {
        const r = d === 0 ? 0 : (drawLen - acc) / d;
        marker = { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * r, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * r };
        drawn.push(marker);
        break;
      }
    }
    return { drawn, marker, color: LINE_COLORS[animCol % LINE_COLORS.length] };
  }, [animCol, drawLen, paths]);

  const nameFontSize = cols > 6 ? 12 : 13.5;

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '15px 18px 12px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>사다리 결과</div>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>이름을 누르거나 “한 명씩 확인”을 눌러 결과를 추적하세요</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={animCol !== null || allDone} onClick={revealNext}>▶ 한 명씩 확인</button>
          <button className="dx-btn dx-btn-soft dx-btn-sm" disabled={animCol !== null || allDone} onClick={revealAllInstant}>전체 결과</button>
          <button className="dx-btn dx-btn-ghost dx-btn-sm" disabled={animCol !== null} onClick={onRegenerate}>다시 섞기</button>
          <button className="dx-btn dx-btn-ghost dx-btn-sm" disabled={animCol !== null} onClick={onReset}>참여자 변경</button>
        </div>
      </div>

      <div style={{ padding: 18, overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${geo.width} ${geo.height}`} width={geo.width} height={geo.height} preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto', maxWidth: geo.width, display: 'block', margin: '0 auto' }}>
          {/* 상단 이름 */}
          {players.map((name, i) => {
            const on = animCol === i || revealed[i] !== undefined;
            const color = LINE_COLORS[i % LINE_COLORS.length];
            return (
              <g key={`top-${i}`} style={{ cursor: animCol === null && revealed[i] === undefined ? 'pointer' : 'default' }}
                onClick={() => animate(i)}>
                <rect x={geo.colX(i) - 40} y={20} width={80} height={34} rx={9}
                  fill={on ? color : '#F1F5F9'} stroke={on ? color : '#E2E8F0'} />
                <text x={geo.colX(i)} y={41} textAnchor="middle" fontSize={nameFontSize} fontWeight={700}
                  fill={on ? '#fff' : '#334155'}>{name.length > 5 ? name.slice(0, 5) : name}</text>
              </g>
            );
          })}

          {/* 세로줄 */}
          {Array.from({ length: cols }, (_, c) => (
            <line key={`v-${c}`} x1={geo.colX(c)} y1={geo.topY} x2={geo.colX(c)} y2={geo.botY} stroke="#CBD5E1" strokeWidth={3} strokeLinecap="round" />
          ))}
          {/* 가로 발판 */}
          {rungs.map((row, r) => row.map((on, c) => on ? (
            <line key={`h-${r}-${c}`} x1={geo.colX(c)} y1={geo.rowY(r)} x2={geo.colX(c + 1)} y2={geo.rowY(r)} stroke="#94A3B8" strokeWidth={3} strokeLinecap="round" />
          ) : null))}

          {/* 확정된 경로(연하게) */}
          {players.map((_, i) => revealed[i] !== undefined ? (
            <path key={`done-${i}`} d={pointsToPath(paths[i].points)} fill="none"
              stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.32} />
          ) : null)}

          {/* 진행 중 애니메이션 경로 + 마커 */}
          {animGeom && (
            <>
              <path d={pointsToPath(animGeom.drawn)} fill="none" stroke={animGeom.color} strokeWidth={4.5} strokeLinecap="round" strokeLinejoin="round" />
              <circle cx={animGeom.marker.x} cy={animGeom.marker.y} r={8} fill={animGeom.color} stroke="#fff" strokeWidth={2.5} />
            </>
          )}

          {/* 하단 결과 박스 */}
          {slots.map((s, c) => {
            const m = KIND_META[s.kind];
            // 이 결과칸(c)에 도착한 사람이 있으면 강조
            const hitBy = players.findIndex((_, i) => revealed[i] === c);
            const lit = hitBy >= 0;
            return (
              <g key={`res-${c}`}>
                <rect x={geo.colX(c) - 42} y={geo.botY + 8} width={84} height={40} rx={9}
                  fill={lit ? m.bg : '#FAFAFA'} stroke={lit ? m.color : '#E5E7EB'} strokeWidth={lit ? 2 : 1} />
                <text x={geo.colX(c)} y={geo.botY + 26} textAnchor="middle" fontSize={11} fontWeight={700} fill={m.color}>{m.emoji} {m.label}</text>
                <text x={geo.colX(c)} y={geo.botY + 40} textAnchor="middle" fontSize={10.5} fill={lit ? '#334155' : '#94A3B8'}>
                  {s.label.length > 8 ? `${s.label.slice(0, 8)}…` : s.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* 결과 요약 리스트 */}
      <div style={{ padding: '4px 18px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
        {players.map((name, i) => {
          const end = revealed[i];
          const done = end !== undefined;
          const slot = done ? slots[end] : null;
          const m = slot ? KIND_META[slot.kind] : null;
          const color = LINE_COLORS[i % LINE_COLORS.length];
          return (
            <div key={`sum-${i}`} onClick={() => animate(i)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
              border: `1px solid ${done ? (m?.color ?? '#E5E7EB') : '#E5E7EB'}`, background: done ? (m?.bg ?? '#fff') : '#fff',
              cursor: animCol === null && !done ? 'pointer' : 'default', opacity: animCol !== null && animCol !== i && !done ? 0.5 : 1,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 700, flexShrink: 0, maxWidth: 72, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: done ? (m?.color ?? '#334155') : '#CBD5E1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                title={done ? `${m?.label} · ${slot?.label}` : undefined}>
                {done ? `${m?.emoji} ${slot?.label}` : '???'}
              </span>
            </div>
          );
        })}
      </div>

      {allDone && (
        <div style={{ padding: '0 18px 18px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#ECFDF5', color: '#047857', padding: '10px 18px', borderRadius: 999, fontSize: 13.5, fontWeight: 700 }}>
            🎉 모든 결과가 공개되었습니다!
          </div>
        </div>
      )}
    </div>
  );
}
